import * as React from 'react';
import PropTypes from 'prop-types';
import { get, isEqual } from 'lodash';
import classnames from 'classnames';
import cuid from 'cuid';

import SecondaryButton from '../Button/SecondaryButton';
import ChoicesField from './ChoicesField';
import RichTextField from './RichTextField';
import { FileField } from './FileField';
import Text from './Text';
import Image from './Image';
import Table from './Table';
import Avatar from '../Avatar';
import Users from '../Users';

import { ReactComponent as PinIcon } from '../../icons/icon-pin.svg';
import { ReactComponent as PinFilledIcon } from '../../icons/icon-pin-filled.svg';

import '../../css/pin.scss';

import {
  TEXT,
  IMAGE,
  CHOICES_FIELD,
  CODE_FIELD,
  RICH_TEXT_FIELD,
  INSTRUCTIONS,
  TABLE_FIELD,
  FILE_FIELD,
} from '../../constants';

const CodeField = React.lazy(() => import('./CodeField'));

const DoNotRollupBlockComponents = [TEXT, IMAGE, INSTRUCTIONS];

const MAPPING = {
  [TEXT]: Text,
  [CHOICES_FIELD]: ChoicesField,
  [RICH_TEXT_FIELD]: RichTextField,
  [IMAGE]: Image,
  [TABLE_FIELD]: Table,
  [CODE_FIELD]: CodeField,
  [FILE_FIELD]: FileField,
};

const UI_OPTIONS = {
  [INSTRUCTIONS]: {
    [TEXT]: {
      instructions: true,
    },
  },
};

const PinButton = ({ onClick, pinState }) => (
  <div className={classnames('pin-icon', { filled: pinState })}>
    <a href="#" onClick={onClick}>
      {pinState ? <PinFilledIcon /> : <PinIcon />}
    </a>
  </div>
);

class ListeningComponent extends React.Component {
  constructor(props) {
    super(props);
    const { blockComponentId, doc } = props;
    this.state = { data: doc.data[blockComponentId] };
  }

  componentDidMount() {
    const { register, docId, blockComponentId } = this.props;
    register(docId, blockComponentId, (newData) => {
      this.setState({ data: newData });
    });
  }

  isStringEmpty = (data) => {
    // we first determine if it's rich text, then whether there is something
    // parseable in the data structure, and then look for any op with text
    // content which is longer than 0
    if (!data) {
      return true;
    }
    if (!Array.isArray(data.ops)) {
      return true;
    }
    return !data.ops.some(
      (op) => typeof op.insert === 'string' && op.insert.trim().length > 0
    );
  };

  isCodeEmpty = (data) => {
    // value is the default contents from the template, we want to compare
    // against this to check if the trimmed template value (excluding white
    // space) is identical to the trimmed actual value, or if the trimmed
    // actual value is empty
    const { value } = this.props;
    const trimData = ((data && data.code) || '').trim();
    const trimValue = (value || '').trim();
    return trimData.length === 0 || trimData === trimValue;
  };

  // we check if field is empty, currently has functions for rich text and code
  isFieldEmpty = (data, type) =>
    (type === RICH_TEXT_FIELD && this.isStringEmpty(data)) ||
    (type === CODE_FIELD && this.isCodeEmpty(data));

  render() {
    const {
      currentUser,
      Component,
      docId,
      setChangedToNow = () => {},
      setSavedToNow = () => {},
      type,
      ...etc
    } = this.props;
    const { data } = this.state;
    const emptyField = this.isFieldEmpty(data, type);

    return emptyField ? null : (
      <Component
        data={data}
        doc={{
          data: data,
          on: () => {},
          presence: {},
          removeListener: () => {},
        }}
        readOnlyWorksheet
        currentUser={currentUser}
        uniqueName={etc.uuid + docId}
        setChangedToNow={setChangedToNow}
        setSavedToNow={setSavedToNow}
        type={type}
        {...etc}
      />
    );
  }
}

export class UserBar extends React.Component {
  constructor(props) {
    super(props);
    const { doc } = props;
    this.state = { meta: doc.data.meta, users: doc.data.users };
  }

  componentDidMount() {
    const { register, doc } = this.props;
    register(doc.id, 'meta', (newData) => {
      this.setState({ meta: newData });
    });
    register(doc.id, 'users', (newData) => {
      this.setState({ users: newData });
    });
  }

  render() {
    const { meta, users: usersRaw } = this.state;
    // remove instructors and observers from the rollup participant bar
    const users = Object.values(usersRaw).reduce(
      (acc, user) =>
        user.isInstructor || user.noPresence
          ? acc
          : { ...acc, [user.userId]: user },
      {}
    );

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
        className="mb2"
      >
        {meta && meta.avatar ? (
          <Avatar title={meta.name} src={meta.avatar} />
        ) : (
          <Users users={users} allUsers />
        )}
        <div className="body-s text-black-tint-40 ml2">
          {meta && meta.name
            ? meta.name
            : Object.values(users)
                .map((x) => x.name)
                .join(', ')}
        </div>
      </div>
    );
  }
}

const BlockComponent = ({
  type,
  blockType,
  data,
  readOnlyWorksheet,
  currentUser,
  doc,
  docs,
  isRollup,
  register,
  setChangedToNow,
  setSavedToNow,
  generalPath,
  uuid,
  ...etc
}) => {
  const MyComponent = MAPPING[type];
  const path = generalPath ? [generalPath, uuid] : [uuid];

  const [modifiedOnExplorePage, setModifiedOnExplorePage] = React.useState(
    generalPath && !isEqual(doc.data[uuid], get(doc.data, [generalPath, uuid]))
  );

  const [rerenderHash, setRerenderHash] = React.useState(0);
  // For use on the 'Explore' tab to restore the original submission for a component.
  // Currently only used in Code Fields, though future work could extend this functionality to
  // all components.
  const restoreSubmissionOnExplorePage = () => {
    doc.submitOp({ p: [generalPath, uuid], oi: doc.data[uuid] });
    setModifiedOnExplorePage(false);
    setRerenderHash(cuid());
  };
  if (!MyComponent) {
    console.warn(`${type} is not supported.`);
    return null;
  }
  if (generalPath) {
    doc.on('op', (ops) => {
      if (ops.some((op) => isEqual(op.p.slice(0, path.length), path))) {
        setModifiedOnExplorePage(true);
      }
    });
  }

  const uiOptions = get(UI_OPTIONS, [blockType, type], {});

  if (!isRollup || DoNotRollupBlockComponents.includes(type)) {
    return (
      <React.Suspense fallback={null}>
        <MyComponent
          {...etc}
          data={data[uuid]}
          doc={doc}
          readOnlyWorksheet={readOnlyWorksheet}
          currentUser={currentUser}
          uuid={uuid}
          path={path}
          setChangedToNow={setChangedToNow}
          setSavedToNow={setSavedToNow}
          uniqueName={uuid + doc.id}
          rerenderHash={rerenderHash}
          modifiedOnExplorePage={modifiedOnExplorePage}
          onReset={restoreSubmissionOnExplorePage}
          {...uiOptions}
        />
      </React.Suspense>
    );
  }

  return docs
    .sort((x, y) => x.id - y.id)
    .map((x) => {
      const pinLookup = `${x.id}-${uuid}`;
      const pinState = etc.pins[pinLookup];
      if (!pinState && etc.onlyShowPinned) {
        return null;
      }
      return (
        <div className="body mt6 block-container" key={x.id}>
          {currentUser.isInstructor && (
            <PinButton
              onClick={(e) => {
                e.preventDefault();
                etc.changePinState(pinLookup, !pinState);
              }}
              pinState={pinState}
            />
          )}
          <UserBar doc={x} register={register} />
          <ListeningComponent
            docId={x.id}
            blockComponentId={uuid}
            doc={x}
            Component={MyComponent}
            currentUser={currentUser}
            path={path}
            key={path}
            style={{ padding: '0px' }}
            type={type}
            register={register}
            setChangedToNow={setChangedToNow}
            setSavedToNow={setSavedToNow}
            uuid={uuid}
            {...etc}
            {...uiOptions}
          />
        </div>
      );
    });
};

BlockComponent.defaultProps = {
  data: {},
  readOnlyWorksheet: false,
};

BlockComponent.propTypes = {
  type: PropTypes.string.isRequired,
  blockType: PropTypes.string.isRequired,
  data: PropTypes.object,
  readOnlyWorksheet: PropTypes.bool,
  setChangedToNow: PropTypes.func.isRequired,
};

export default BlockComponent;
